import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  SaveAlt as DraftIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { EmailAccountStatus, SupportDraft } from './types';
import { apiService } from '../../services/api';

interface ComposeDialogProps {
  open: boolean;
  onClose: () => void;
  accounts: EmailAccountStatus[];
  /** Pre-fill fields when editing a saved draft */
  existingDraft?: SupportDraft | null;
  /** Called after a successful send so the parent can refresh Sent folder */
  onSent?: () => void;
  /** Called after a draft is saved/updated so the parent can refresh Drafts folder */
  onDraftSaved?: () => void;
}

const DEEP_TEAL = '#1A5F7A';
const WARM_GOLD = '#D4AC0D';

const ComposeDialog: React.FC<ComposeDialogProps> = ({
  open,
  onClose,
  accounts,
  existingDraft,
  onSent,
  onDraftSaved,
}) => {
  const [accountId, setAccountId] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill from existing draft or reset when dialog opens
  useEffect(() => {
    if (!open) return;
    if (existingDraft) {
      setAccountId(existingDraft.email_account_id || (accounts[0]?.id ?? ''));
      setTo(existingDraft.to_address || '');
      setCc(existingDraft.cc || '');
      setSubject(existingDraft.subject || '');
      setBody(existingDraft.body_text || existingDraft.body_html || '');
    } else {
      setAccountId(accounts[0]?.id ?? '');
      setTo('');
      setCc('');
      setSubject('');
      setBody('');
    }
    setError(null);
  }, [open, existingDraft, accounts]);

  const bodyHtml = body.includes('<') ? body : body.replace(/\n/g, '<br>');

  const handleSend = async () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      setError('To, Subject, and body are required');
      return;
    }
    setSending(true);
    setError(null);
    try {
      if (existingDraft) {
        await apiService.sendSupportDraft(existingDraft.id);
      } else {
        await apiService.sendComposeEmail({
          accountId: accountId || undefined,
          to: to.trim(),
          cc: cc.trim() || undefined,
          subject: subject.trim(),
          bodyHtml,
          bodyText: body,
        });
      }
      onSent?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!to.trim()) {
      setError('To address is required to save a draft');
      return;
    }
    setSavingDraft(true);
    setError(null);
    try {
      if (existingDraft) {
        await apiService.updateSupportDraft(existingDraft.id, {
          emailAccountId: accountId || undefined,
          toAddress: to.trim(),
          cc: cc.trim() || undefined,
          subject: subject.trim() || undefined,
          bodyHtml: bodyHtml || undefined,
          bodyText: body || undefined,
        });
      } else {
        await apiService.createSupportDraft({
          emailAccountId: accountId || undefined,
          toAddress: to.trim(),
          cc: cc.trim() || undefined,
          subject: subject.trim() || undefined,
          bodyHtml: bodyHtml || undefined,
          bodyText: body || undefined,
        });
      }
      onDraftSaved?.();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, borderTop: `3px solid ${DEEP_TEAL}` } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: DEEP_TEAL, fontSize: '1.1rem' }}>
          {existingDraft ? 'Edit Draft' : 'New Email'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* From account selector */}
        {accounts.length > 0 && (
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>From</InputLabel>
            <Select value={accountId} label="From" onChange={(e) => setAccountId(e.target.value)}>
              {accounts.map(acc => (
                <MenuItem key={acc.id} value={acc.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DotIcon sx={{ fontSize: 12, color: acc.color }} />
                    {acc.accountName || acc.email} &lt;{acc.email}&gt;
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          size="small"
          label="To"
          placeholder="recipient@example.com"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          sx={{ mb: 1.5 }}
          required
        />

        <TextField
          fullWidth
          size="small"
          label="Cc (optional)"
          placeholder="cc@example.com"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        <TextField
          fullWidth
          size="small"
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mb: 1.5 }}
          required
        />

        <TextField
          fullWidth
          multiline
          rows={12}
          label="Message"
          placeholder="Type your message..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.875rem', lineHeight: 1.6 } }}
        />

        {error && (
          <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={savingDraft ? <CircularProgress size={14} /> : <DraftIcon />}
          onClick={handleSaveDraft}
          disabled={sending || savingDraft}
          sx={{ textTransform: 'none', borderColor: DEEP_TEAL, color: DEEP_TEAL }}
        >
          Save Draft
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={sending || savingDraft} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
          onClick={handleSend}
          disabled={sending || savingDraft || !to.trim() || !subject.trim() || !body.trim()}
          sx={{ bgcolor: WARM_GOLD, color: '#000', '&:hover': { bgcolor: '#b8960a' }, textTransform: 'none', fontWeight: 700 }}
        >
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComposeDialog;
